/**
 * Client Product Selector Component
 * Enhanced product selection with favorites and recent products.
 * Reads from canonical client_products collection via GET /api/products/orderable.
 * Sep 2026 — compact premium cards + product search (presentation only).
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Check, Search, Star, X, Receipt, Home, UserRound, FileWarning, Building2, FileSearch,
  FileText, FolderOpen, Building, Scale, Landmark, TrendingUp, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// UI-002: subtle per-product icon + tint (keyword match, restrained palette)
const PRODUCT_ICONS = [
  [/tax/i, Receipt, 'bg-emerald-50 text-emerald-600'],
  [/mls update/i, TrendingUp, 'bg-violet-50 text-violet-600'],
  [/mls/i, Home, 'bg-indigo-50 text-indigo-600'],
  [/current owner/i, UserRound, 'bg-sky-50 text-sky-600'],
  [/avr/i, FileWarning, 'bg-rose-50 text-rose-600'],
  [/cos/i, Building2, 'bg-blue-50 text-blue-600'],
  [/commercial/i, FileSearch, 'bg-emerald-50 text-emerald-600'],
  [/deed/i, FileText, 'bg-indigo-50 text-indigo-600'],
  [/full search/i, FolderOpen, 'bg-amber-50 text-amber-600'],
  [/hoa/i, Building, 'bg-fuchsia-50 text-fuchsia-600'],
  [/legal|vesting/i, Scale, 'bg-indigo-50 text-indigo-600'],
  [/mortgage/i, Landmark, 'bg-emerald-50 text-emerald-600'],
  [/update/i, RefreshCw, 'bg-violet-50 text-violet-600'],
];
const productVisual = (name) => {
  const hit = PRODUCT_ICONS.find(([re]) => re.test(name));
  return hit ? { Icon: hit[1], tint: hit[2] } : { Icon: FileText, tint: 'bg-slate-100 text-slate-600' };
};

/**
 * Product Tile Component — compact premium card, blue/violet ring + check when selected
 */
const ProductTile = ({ product, isSelected, isFavorite, onSelect }) => {
  const { Icon, tint } = productVisual(product);
  return (
    <button
      type="button"
      onClick={() => onSelect(product)}
      className={`
        co-tile group relative flex items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left
        focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300
        ${isSelected
          ? 'border-indigo-400 bg-gradient-to-br from-blue-50 to-violet-50 shadow-[0_0_0_1px_rgba(99,102,241,0.35),0_6px_14px_-8px_rgba(79,70,229,0.5)]'
          : 'border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-indigo-200 hover:shadow-[0_6px_14px_-8px_rgba(15,23,42,0.25)]'}
      `}
      tabIndex={0}
      role="option"
      aria-selected={isSelected}
      aria-label={`${product}${isFavorite ? ' (Favorite)' : ''}`}
      data-testid={`product-tile-${product.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isSelected ? 'bg-gradient-to-br from-blue-600 to-violet-600 text-white' : tint}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className={`min-w-0 flex-1 truncate text-[13px] font-semibold leading-5 ${isSelected ? 'text-indigo-700' : 'text-slate-800'}`}>
        {product}
      </span>
      {isSelected ? (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-sm" data-testid={`product-tile-selected-${product.replace(/\s+/g, '-').toLowerCase()}`}>
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      ) : isFavorite ? (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-500">
          <Star className="h-3 w-3" fill="currentColor" />
        </span>
      ) : null}
    </button>
  );
};

const SectionHeader = ({ icon, title, count }) => (
  <div className="flex items-center gap-1.5">
    {icon}
    <h4 className="text-[13px] font-semibold text-slate-800">{title}</h4>
    {typeof count === 'number' && (
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{count}</span>
    )}
  </div>
);

/**
 * Client Product Selector Component
 */
export const ClientProductSelector = ({ value, onSelect, user, onCountChange }) => {
  const [recentProducts, setRecentProducts] = useState([]);
  const [rawFavorites, setRawFavorites] = useState(['Tax Search', 'MLS', 'Current Owner Search']);
  const [allProducts, setAllProducts] = useState([]);
  const [productMeta, setProductMeta] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [query, setQuery] = useState('');

  // Derived: favorites filtered to only include products that actually exist in the orderable list
  const favoriteProducts = React.useMemo(() => {
    if (allProducts.length === 0) return [];
    const orderableSet = new Set(allProducts.map(p => p.toLowerCase()));
    return rawFavorites.filter(f => orderableSet.has(f.toLowerCase()));
  }, [rawFavorites, allProducts]);

  // Derived: "All Products" minus what's already in Popular Choices (no duplicates)
  const remainingProducts = React.useMemo(() => {
    const favSet = new Set(favoriteProducts.map(f => f.toLowerCase()));
    return allProducts.filter(p => !favSet.has(p.toLowerCase()));
  }, [allProducts, favoriteProducts]);

  const q = query.trim().toLowerCase();
  const visibleProducts = q ? remainingProducts.filter(p => p.toLowerCase().includes(q)) : remainingProducts;

  // UI-002: mirror the "All Products" count in the parent section header pill
  useEffect(() => {
    if (!loadingProducts) onCountChange?.(remainingProducts.length);
  }, [remainingProducts, loadingProducts, onCountChange]);

  useEffect(() => {
    fetchOrderableProducts();
    if (user?.id) {
      fetchRecentProducts();
    }
    fetchFavoriteProducts();
  }, [user]);

  const fetchOrderableProducts = async () => {
    setLoadingProducts(true);
    setProductsError(null);
    try {
      const response = await axios.get(`${API}/products/orderable`);
      const products = response.data.products || [];
      setAllProducts(products.map(p => p.name));
      setProductMeta(Object.fromEntries(products.map(p => [p.name, { workflow_type: p.workflow_type || null }])));
    } catch (error) {
      console.error('Error fetching orderable products:', error);
      setAllProducts([]);
      setProductsError(error?.response?.data?.detail || 'Products unavailable');
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchRecentProducts = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/recent-products`);
      setRecentProducts(response.data.recent_products || []);
    } catch (error) {
      console.error('Error fetching recent products:', error);
    }
  };

  const fetchFavoriteProducts = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      if (response.data.favorites && response.data.favorites.length > 0) {
        setRawFavorites(response.data.favorites);
      }
    } catch (error) {
      console.error('Error fetching favorite products:', error);
    }
  };

  const handleSelect = async (productName) => {
    onSelect(productName, productMeta[productName] || null);
    
    if (user?.id) {
      try {
        await axios.post(`${API}/users/${user.id}/recent-products`, null, {
          params: { product_name: productName }
        });
        fetchRecentProducts();
      } catch (error) {
        console.error('Error tracking recent product:', error);
      }
    }
  };

  return (
    <div className="space-y-3" data-testid="client-product-selector">
      {/* Favorites Section */}
      {favoriteProducts.length > 0 && (
        <div className="space-y-2">
          <SectionHeader
            icon={<Star className="h-3.5 w-3.5 text-amber-500" />}
            title="Popular Choices"
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {favoriteProducts.map((product) => (
              <ProductTile 
                key={`fav-${product}`} 
                product={product} 
                isSelected={value === product}
                isFavorite={true}
                onSelect={handleSelect}
              />
            ))}
          </div>
        </div>
      )}

      {/* All Products Grid — excludes products already shown in Popular Choices */}
      <div className="space-y-2">
        <SectionHeader
          icon={<span className="h-2 w-2 rounded-full bg-slate-400"></span>}
          title="All Products"
          count={!loadingProducts ? remainingProducts.length : undefined}
        />
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products..."
            className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50/60 pl-8 pr-8 text-sm text-slate-800 placeholder:text-slate-400 focus:bg-white"
            data-testid="product-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Clear product search"
              data-testid="product-search-clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {loadingProducts ? (
            <div className="col-span-full py-3 text-center text-sm text-slate-400">Loading products...</div>
          ) : productsError ? (
            <div className="col-span-full flex items-center justify-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800" data-testid="products-unavailable">
              <span>Products unavailable — {String(productsError)}</span>
              <button type="button" onClick={fetchOrderableProducts} className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100" data-testid="products-retry-button">
                Retry
              </button>
            </div>
          ) : visibleProducts.length > 0 ? visibleProducts.map((product) => (
            <ProductTile 
              key={product} 
              product={product}
              isSelected={value === product}
              isFavorite={false}
              onSelect={handleSelect}
            />
          )) : (
            <div className="col-span-full py-3 text-center text-sm text-slate-400" data-testid="product-search-empty">
              {q ? `No products match "${query}"` : 'All products are shown in Popular Choices above'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientProductSelector;
